import { ApiError, ValidationApiError } from '../interfaces/api-error';

const FALLBACK_MESSAGES: Record<ApiError['error'], string> = {
  'bad-request': 'Controleer de invoer en probeer opnieuw.',
  forbidden: 'Je hebt geen rechten om deze actie uit te voeren.',
  network: 'Kan geen verbinding maken met de server. Controleer je internetverbinding en probeer opnieuw.',
  'not-found': 'De gevraagde gegevens konden niet worden gevonden.',
  server: 'Er is iets misgegaan op de server. Probeer het later opnieuw.',
  unauthorized: 'Je sessie is verlopen. Log opnieuw in.',
  validation: 'Controleer de invoer en probeer opnieuw.',
};

const DUTCH_WORD_PATTERN = /\b(actie|acties|aanmaken|bestaat|controleer|domein|e-mailadres|deze|dit|filiaal|fout|geen|gebruiker|geldig|geldige|ging|hebt|konden|laden|later|maken|maximaal|minimaal|mislukt|naam|niet|nog|opgeslagen|opslaan|opgetreden|opnieuw|organisatie|postcode|probeer|rol|selecteer|telefoonnummer|traject|veld|verlopen|verplicht|verplichte|verwijderen|worden)\b/i;
const TECHNICAL_MESSAGE_PATTERN = /(\^|\$|\\\w|\[|\]|\(\?|regex|regexp|regular expression|pattern|must match|should match|does not match|validation failed for|failed to fetch|networkerror|typeerror|invalid_type|too_small|too_big|expected|required|string must|number must)/i;

const FIELD_VALIDATION_MESSAGES: Record<string, string> = {
  email: 'Voer een geldig e-mailadres in.',
  emailAddress: 'Voer een geldig e-mailadres in.',
  firstName: 'Naam bevat ongeldige tekens. Gebruik letters, spaties, apostrofs, punten, komma\'s, streepjes of schuine strepen.',
  mail: 'Voer een geldig e-mailadres in.',
  name: 'Naam bevat ongeldige tekens. Controleer de toegestane tekens en probeer opnieuw.',
  lastName: 'Naam bevat ongeldige tekens. Gebruik letters, spaties, apostrofs, punten, komma\'s, streepjes of schuine strepen.',
  phone: 'Voer een geldig telefoonnummer in.',
  phoneNumber: 'Voer een geldig telefoonnummer in.',
  postalcode: 'Voer een geldige postcode in.',
  postalCode: 'Voer een geldige postcode in.',
  postcode: 'Voer een geldige postcode in.',
  domain: 'Voer een geldig domein in.',
  duration: 'Afronden binnen bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties en streepjes.',
  fieldName: 'Veldnaam bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties en streepjes.',
  invoiceAmount: 'Factuurbedrag is ongeldig. Gebruik cijfers met maximaal twee decimalen.',
  title: 'Titel bevat ongeldige tekens. Controleer de toegestane tekens en probeer opnieuw.',
  type: 'Type is ongeldig. Kies AGROS, CLIENT, EMPLOYER of DEVED.',
};

const BUSINESS_VALIDATION_MESSAGES = new Map<string, string>([
  [
    'Selected clientManagerId is not assigned to the trajectory',
    'De geselecteerde klantmanager is niet gekoppeld aan het traject. Kies een gekoppelde klantmanager of verwijder de selectie.',
  ],
  [
    'Selected consultantId is not assigned to the trajectory',
    'De geselecteerde consulent is niet gekoppeld aan het traject. Kies een gekoppelde consulent of verwijder de selectie.',
  ],
  [
    'Selected client manager is no longer eligible for the case organisation',
    'de geselecteerde klantmanager heeft geen toegang meer tot deze case of organisatie',
  ],
  [
    'Selected consultant is no longer eligible for the case organisation',
    'De geselecteerde consulent mag niet meer aan deze organisatie worden gekoppeld. Kies een andere consulent of verwijder de selectie.',
  ],
  [
    'organisationId must be the trajectory organisation or a direct child organisation',
    'De organisatie hoort niet bij het gekozen traject. Kies de trajectorganisatie of een directe onderliggende organisatie.',
  ],
  [
    'Selected trajectory has no organisation',
    'Het gekozen traject heeft geen organisatie. Kies een ander traject of laat eerst een organisatie aan dit traject koppelen.',
  ],
]);

const normalizeMessage = (message: unknown): string | null => (
  typeof message === 'string' && message.trim().length > 0 ? message.trim() : null
);

const getBusinessValidationMessage = (message: string | null): string | undefined => (
  message
    ? BUSINESS_VALIDATION_MESSAGES.get(message.replace(/^Validation failed for [^:\r\n]+:\s*/i, '').trim())
    : undefined
);

const isDutchUserFacingMessage = (message: string): boolean => (
  DUTCH_WORD_PATTERN.test(message) && !TECHNICAL_MESSAGE_PATTERN.test(message)
);

const getIssueField = (path: unknown): string | null => {
  if (!Array.isArray(path) || path.length === 0) {
    return null;
  }

  const lastSegment = path[path.length - 1];
  if (typeof lastSegment !== 'string') {
    return null;
  }

  return lastSegment.split('.').filter(Boolean).pop() ?? null;
};

const getPatternValidationMessage = (field: string | null, message: string | null): string | null => {
  if (!message || !TECHNICAL_MESSAGE_PATTERN.test(message)) {
    return null;
  }

  if (message.includes('a-zA-Z0-9\\s\\-_')) {
    return 'Naam bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties, streepjes en underscores.';
  }

  if (message.includes('a-zA-Z0-9\\s\\-&.,()/')) {
    return 'Naam bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties, streepjes, &, punten, komma\'s, haakjes en schuine strepen.';
  }

  if (message.includes('a-zA-Z0-9\\s\\-&.,()')) {
    if (field === 'title') {
      return 'Titel bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties, streepjes, &, punten, komma\'s en haakjes.';
    }

    return 'Naam bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties, streepjes, &, punten, komma\'s en haakjes.';
  }

  if (message.includes('a-zA-Z0-9\\s\\-')) {
    return field === 'duration'
      ? FIELD_VALIDATION_MESSAGES.duration
      : 'Dit veld bevat ongeldige tekens. Gebruik alleen letters, cijfers, spaties en streepjes.';
  }

  if (message.includes('[0-9]+([.,][0-9]{1,2})')) {
    return FIELD_VALIDATION_MESSAGES.invoiceAmount;
  }

  if (message.includes('A-Za-z0-9+_.-') && (field === 'email' || field === 'emailAddress' || field === 'mail')) {
    return FIELD_VALIDATION_MESSAGES.email;
  }

  if (message.includes('[1-9][0-9]{3}\\s?[A-Za-z]{2}')) {
    return 'Voer een geldige postcode in, bijvoorbeeld 1234 AB.';
  }

  if (message.includes('A-Za-z0-9-') && field === 'domain') {
    return 'Voer een geldig domein in, bijvoorbeeld voorbeeld.nl.';
  }

  if (message.includes('AGROS|CLIENT|EMPLOYER|DEVED')) {
    return FIELD_VALIDATION_MESSAGES.type;
  }

  if (message.includes('\\p{L}\\p{M}')) {
    return 'Naam bevat ongeldige tekens. Gebruik letters, spaties, apostrofs, punten, komma\'s, streepjes of schuine strepen.';
  }

  return null;
};

const sanitizeValidationIssue = (issue: ValidationApiError['issues'][number]) => {
  const originalMessage = normalizeMessage(issue.message);
  if (originalMessage && isDutchUserFacingMessage(originalMessage)) {
    return issue;
  }

  const field = getIssueField(issue.path);
  const lengthLimit = field === 'fieldName' && originalMessage?.match(/at most (\d+) characters/i)?.[1];
  const localizedMessage = getBusinessValidationMessage(originalMessage) ?? (lengthLimit
    ? `Veldnaam mag maximaal ${lengthLimit} tekens bevatten. Kort de vraag in.`
    : null)
    ?? getPatternValidationMessage(field, originalMessage)
    ?? (field ? FIELD_VALIDATION_MESSAGES[field] : undefined);

  return {
    ...issue,
    message: localizedMessage ?? FALLBACK_MESSAGES.validation,
  };
};

const getPreservedOrFallbackMessage = (message: unknown, fallback: string): string => {
  const normalizedMessage = normalizeMessage(message);
  if (!normalizedMessage) {
    return fallback;
  }

  if (isDutchUserFacingMessage(normalizedMessage)) {
    return normalizedMessage;
  }

  return fallback;
};

const toUserFacingApiError = (apiError: ApiError): ApiError => {
  if (apiError.error === 'validation') {
    const issues = apiError.issues.map(sanitizeValidationIssue);
    const message = getPreservedOrFallbackMessage(
      apiError.message,
      issues[0]?.message ?? getBusinessValidationMessage(normalizeMessage(apiError.message)) ?? FALLBACK_MESSAGES.validation,
    );

    return {
      ...apiError,
      message,
      issues,
    };
  }

  return {
    ...apiError,
    message: getPreservedOrFallbackMessage(apiError.message, FALLBACK_MESSAGES[apiError.error]),
  } as ApiError;
};

export class RequestError extends Error {
  readonly apiError: ApiError;

  constructor(apiError: ApiError) {
    const userFacingApiError = toUserFacingApiError(apiError);
    const message = 'message' in userFacingApiError && typeof userFacingApiError.message === 'string' && userFacingApiError.message.length > 0
      ? userFacingApiError.message
      : FALLBACK_MESSAGES[userFacingApiError.error];

    super(message);
    this.apiError = userFacingApiError;
  }
}
